import * as React from "react";
import { cva } from "class-variance-authority";

import { cn } from "../../lib/utils";

/**
 * shadcn/ui Alert.
 *
 * Two deliberate deviations from the stock component, both for Arabic:
 *
 * 1. The icon is laid out with flex rather than `position: absolute; left: 1rem`
 *    plus a `pl-11` text indent. The stock rule is physical, so in RTL the icon
 *    sits on top of the text; flex + `gap` mirrors on its own.
 * 2. `status` and `warning` variants are added for this portal's palette. The
 *    stock `destructive` variant is kept and points at the shared token.
 */
const alertVariants = cva(
  "relative flex w-full items-start gap-2.5 rounded-lg border px-4 py-3 text-sm",
  {
    variants: {
      variant: {
        default: "bg-background text-foreground",
        destructive:
          "border-destructive/50 text-destructive [&>svg]:text-destructive",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const Alert = React.forwardRef(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant }), className)}
    {...props}
  />
));
Alert.displayName = "Alert";

const AlertTitle = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("font-semibold leading-5 tracking-tight", className)}
    {...props}
  />
));
AlertTitle.displayName = "AlertTitle";

const AlertDescription = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("leading-5", className)} {...props} />
));
AlertDescription.displayName = "AlertDescription";

export { Alert, AlertTitle, AlertDescription, alertVariants };
